import { Search2Icon } from '@chakra-ui/icons'
import {
  CloseButton,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  MenuItem,
  MenuList,
  Skeleton,
  Text
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import AvatarActivity from '@/shared/components/avatar-activity'
import Api from '@/shared/services/api'
import { GroupUsersByFeedGroup } from '@/shared/services/api/access-profile/list-group-user-profiles'
import EmptyComponent from '@/ui/components/Empty/EmptyComponent'
import { searchFilter } from '@/ui/utils/searchFilter'
import { textSort } from '@/ui/utils/sorts'

export default function UserGroupMembersMenuList({
  userGroup,
  onClose
}: {
  userGroup: GroupUsersByFeedGroup
  onClose: () => void
}) {
  const { t } = useTranslation()

  const [searchTerm, setSearchTerm] = useState('')

  const userGroupProfilesQuery = useQuery({
    queryKey: ['groupUserProfiles', userGroup],
    queryFn: async () => {
      const [response, error] = await Api.listGroupUserProfiles(
        userGroup.id.toString()
      )
      if (error) throw error

      const userGroupsResponse = response.data.toSorted(
        ({ username: nameA }, { username: nameB }) => textSort(nameA, nameB)
      )
      return userGroupsResponse
    }
  })

  const filteredUserProfiles = searchFilter({
    data: userGroupProfilesQuery.data ?? [],
    search: searchTerm,
    filters: [(data) => data.username, (data) => data.avatarName]
  })

  return (
    <MenuList zIndex={1800}>
      <Flex
        justify={'space-between'}
        align={'center'}
        borderBottom={'1px'}
        borderColor={'custom.gray.700'}
        pl={4}
        pr={1}
        pb={2}
        w={'full'}
      >
        <Text>{userGroup.name}</Text>
        <CloseButton onClick={onClose} />
      </Flex>
      <Flex>
        {userGroupProfilesQuery.data &&
          userGroupProfilesQuery.data?.length >= 5 && (
            <InputGroup width={300} marginBottom={4}>
              <InputLeftElement display={'flex'} top={'-9px'} left={'-6px'}>
                <Search2Icon />
              </InputLeftElement>
              <Input
                variant="unstyled"
                placeholder={t('search')}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
              />
            </InputGroup>
          )}
        {userGroupProfilesQuery.isLoading ? (
          <Flex direction={'column'} gap={2} p={4} w={'full'}>
            {Array.from({ length: 2 }).map((_, index) => (
              <Skeleton key={index} h={'2.75rem'} w={'full'} />
            ))}
          </Flex>
        ) : userGroupProfilesQuery.isError ? (
          <>Erro</>
        ) : userGroupProfilesQuery.data?.length === 0 ? (
          <EmptyComponent
            iconWidth="16"
            iconHeight="16"
            fontSize={'16px'}
            height={'15vh'}
          />
        ) : (
          <Flex
            overflow={'auto'}
            flex={'1'}
            flexDir={'column'}
            pr={'1'}
            maxH={'10rem'}
          >
            {filteredUserProfiles.length === 0 ? (
              <EmptyComponent
                iconWidth="16"
                iconHeight="16"
                fontSize={'16px'}
                height={'15vh'}
              />
            ) : (
              filteredUserProfiles.map((profile) => (
                <MenuItem mt={'8px'} key={profile.userId}>
                  <Flex gap={'16px'} mb={'8px'} alignItems={'center'}>
                    <AvatarActivity
                      activity={'gray'}
                      size={'sm'}
                      imageUrl={profile.avatarUrl}
                      name={profile.username.trim()}
                    />
                    <Flex flexDir={'column'}>
                      <Text fontSize={'1rem'} fontWeight={'400'}>
                        {profile.avatarName}
                      </Text>
                      <Text fontSize={'0.75rem'} fontWeight={'400'}>
                        {profile.username}
                      </Text>
                      <Text fontSize={'0.75rem'} fontWeight={'400'}>
                        {t(
                          `profile.profileType.${
                            userGroup.personaType.toLowerCase() as Lowercase<
                              typeof userGroup.personaType
                            >
                          }`
                        )}
                      </Text>
                    </Flex>
                  </Flex>
                </MenuItem>
              ))
            )}
          </Flex>
        )}
      </Flex>
    </MenuList>
  )
}
